import { Toast, ToastBody, type ToastIntent, ToastTitle, useToastController } from '@fluentui/react-components';
import { type ReactNode, useCallback } from 'react';

/**
 * Custom hook for displaying toast notifications.
 *
 * @returns A function that can be used to display a toast notification.
 * @param title - The title of the toast notification.
 * @param message - The message of the toast notification.
 * @param intent - The intent of the toast notification. Can be one of 'success', 'warning', 'info', or 'error'.
 * @param timeout - The time in milliseconds before the toast notification is automatically dismissed.
 */
export function useToast() {
    const { dispatchToast } = useToastController('toast');

    return useCallback(
        (
            title: ReactNode,
            message: ReactNode,
            options?: { intent?: ToastIntent; timeout?: number; toastId?: string }
        ) =>
            dispatchToast(
                <Toast>
                    <ToastTitle>{title}</ToastTitle>
                    <ToastBody>{message}</ToastBody>
                </Toast>,
                options
            ),
        [dispatchToast]
    );
}
